import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import { formatApiError } from "@/lib/utils-app";
import { useUserPlanTier } from "@/hooks/useUserPlanTier";
import { isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import { toast } from "sonner";

export default function ListDogDialog({ open, onClose, dogId }) {
  const [tier, setTier] = useState("breeding_listing_30d");
  const [description, setDescription] = useState("");
  const [fee, setFee] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [promo, setPromo] = useState(null);
  // Pro & Elite get unlimited free breeding listings (server-side
  // verified); we surface that on the UI so they don't see a price
  // tag for something they aren't charged for.
  const { isProOrElite, isElite } = useUserPlanTier();

  const tiers = [
    { id: "breeding_listing_30d", label: "30 days", price: "$14.99", amount: 14.99 },
    { id: "breeding_listing_90d", label: "90 days", price: "$29.99", amount: 29.99 },
    { id: "breeding_listing_1y", label: "1 year", price: "$49.99", amount: 49.99 },
  ];

  const tierAmount = (tiers.find((t) => t.id === tier) || {}).amount || 14.99;

  // Reset promo when tier changes (different base amount)
  useEffect(() => {
    setPromo(null);
  }, [tier]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: tier,
        origin_url: webOrigin(),
        dog_id: dogId,
        listing_description: description,
        listing_fee_requested: parseFloat(fee) || 0,
        ...(promo?.valid ? { promo_code: promo.code } : {}),
      });
      if (data.free) {
        toast.success("Listing unlocked");
        onClose();
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>List for breeding</DialogTitle>
        </DialogHeader>
        {isIOS() && !isProOrElite ? (
          <IosPurchaseNotice label="Breeding directory listing" />
        ) : (
        <div className="space-y-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 block">
              Listing term
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {tiers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTier(t.id)}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    tier === t.id
                      ? "border-[#C1272D] bg-[#C1272D]/5 text-[#0E0E0E]"
                      : "border-[#E5E5E5] bg-white text-[#6B6B6B] hover:border-[#0E0E0E]/20"
                  }`}
                  data-testid={`tier-${t.id}`}
                >
                  <div className="font-semibold text-ink">{t.label}</div>
                  <div className="text-xs mt-1">
                    {isProOrElite ? <span className="text-emerald-700 font-semibold">Free with plan</span> : t.price}
                  </div>
                  {isElite && t.id === "breeding_listing_30d" && (
                    <div className="text-[10px] text-gold-dark mt-0.5">+ featured · auto-renew</div>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Description
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Temperament, working lines, stud fee terms, location…"
              className="mt-1"
              data-testid="listing-description"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Stud fee requested (optional, USD)
            </Label>
            <Input
              type="number"
              value={fee}
              min="0"
              step="0.01"
              onChange={(e) => setFee(e.target.value)}
              placeholder="0"
              className="mt-1 rounded-lg"
              data-testid="listing-fee"
            />
          </div>
          <p className="text-xs text-[#6B6B6B]">
            {isProOrElite
              ? "Included with your plan — no Stripe redirect, the listing publishes instantly."
              : "You'll be taken to secure Stripe checkout. Payment goes to Armstrong K9 Registry; any stud fee is negotiated directly off-platform."}
          </p>
          {!isProOrElite && (
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1 block">
              Promo code (optional)
            </Label>
            <CompBanner className="mb-3" />
            <PromoCodeInput
              kind="breeding_listing"
              amount={tierAmount}
              applied={promo}
              onApplied={(p) => {
                setPromo(p);
                toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
              }}
              onCleared={() => setPromo(null)}
            />
          </div>
          )}
        </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">
            Cancel
          </Button>
          {!(isIOS() && !isProOrElite) && (
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="checkout-listing-btn"
          >
            {submitting
              ? (isProOrElite ? "Publishing…" : "Redirecting…")
              : isProOrElite
              ? "Publish for free"
              : promo?.valid
              ? `Pay $${promo.final.toFixed(2)} & list`
              : "Continue to payment"}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
